import type {
  GTMCustomEvent,
  GTMEvent,
  GTMPurchaseParams,
} from '@skin-seoul/utils';

/**
 * Free-form passthrough for add_to_cart dataLayer payloads.
 *
 * Master pre-migration pushed a flat shape `{event: 'add_to_cart',
 * 'x-fb-cd-content_type', 'x-fb-cd-content_name', 'x-fb-cd-content_ids',
 * value, currency, items: [...], event_name: 'add_to_cart', event_time,
 * item_variant, product_sku, ...}` via `logger.click('add_to_cart', ...)` →
 * `sendGTMEvent`. The caller builds this full shape (typically via
 * `createAddToCartParams` for PDP, or inline for quick-add cards) and passes
 * it through. We preserve that contract so downstream GTM variable mappings
 * (`{{dlv - items}}`, `{{dlv - x-fb-cd-content_ids}}`, etc.) continue to
 * resolve against the top-level fields.
 */
export type TrackAddToCartGtmExtras = Record<string, unknown>;

/**
 * Free-form passthrough for view_item dataLayer payloads.
 *
 * Same pattern as add_to_cart — master pushed `{event: 'page_view',
 * event_name: 'view_item', ...}` flat via `LoggingView`. Caller (PDP page)
 * builds the shape via `createPDPViewItemParams` and passes it through.
 */
export type TrackViewGtmExtras = Record<string, unknown>;

export const mapAddToCart = (
  _params: { productId: string; quantity?: number; variant?: string },
  extras?: TrackAddToCartGtmExtras,
): GTMCustomEvent => ({
  ...(extras ?? {}),
  event: 'add_to_cart',
});

export const mapViewItem = (
  _params: { productId: string },
  extras?: TrackViewGtmExtras,
): GTMCustomEvent => ({
  ...(extras ?? {}),
  event: 'page_view',
  event_name: 'view_item',
});

export const mapPurchase = (
  params: GTMPurchaseParams & { orderId: string },
): GTMEvent => ({
  event: 'purchase',
  ecommerce: params.ecommerce,
  user: params.user,
  platform: params.platform,
  // Master didn't emit `express_checkout` at all for normal checkout — only
  // express flow included it. Spread conditionally to avoid `undefined` keys
  // leaking into the dataLayer.
  ...(params.express_checkout ? { express_checkout: params.express_checkout } : {}),
});
